import type { SchemaProperty } from 'types/schema';
import type { FastifyInstance } from 'fastify';

/**
 * Names of the schema properties the caller may not read. Zero permission calls when no
 * property declares `access.read`. Fail-closed: an empty allowed Set (the error path of
 * `checkReadPermissions`) denies every gated property.
 */
export async function resolveReadDeniedPropertyNames(params: {
  schemaProperties: SchemaProperty[];
  /**
   * Checks which of the given permissions the caller holds on the sphere.
   * Returns the Set of allowed permissions. Must be fail-closed: return an empty Set
   * (not throw) on SpiceDB errors so that denied behavior is the safe default.
   */
  checkReadPermissions: (permissions: string[]) => Promise<Set<string>>;
}): Promise<Set<string>> {
  const readGated = params.schemaProperties.filter((p) => p.access?.read);
  if (readGated.length === 0) {
    return new Set();
  }

  // Deduplicate permissions — many properties may share the same permission string
  const distinctPerms = [...new Set(readGated.map((p) => p.access!.read!))];

  // One call for all distinct permissions (fail-closed — errors → empty set)
  const allowedPerms = await params.checkReadPermissions(distinctPerms);

  return new Set(readGated.filter((p) => !allowedPerms.has(p.access!.read!)).map((p) => p.name));
}

/**
 * Strip properties from a sphere's `properties` bag based on the caller's read permissions.
 * No-op (zero permission calls) when no schema property declares `access.read`.
 * Fail-closed: if `checkReadPermissions` returns an empty Set (error path), the property is stripped.
 */
export async function applyAccessReadFilter(params: {
  sphere: any;
  schemaProperties: SchemaProperty[];
  checkReadPermissions: (permissions: string[]) => Promise<Set<string>>;
  fastify?: FastifyInstance;
  /**
   * Receives the denied set this filter computed, so a caller that needs it (the effective
   * Archetype read) uses the one evaluation that decided what was stripped instead of asking again.
   */
  onDenied?: (denied: ReadonlySet<string>) => void;
}): Promise<any> {
  const { sphere, schemaProperties, checkReadPermissions, fastify, onDenied } = params;

  // Zero-cost path: no read-gated properties — return the original reference unchanged
  const readGated = schemaProperties.filter((p) => p.access?.read);
  if (readGated.length === 0) {
    onDenied?.(new Set());
    return sphere;
  }

  const denied = await resolveReadDeniedPropertyNames({ schemaProperties, checkReadPermissions });
  onDenied?.(denied);

  // Build a shallow copy of properties, then delete denied ones
  const filteredProperties = { ...sphere.properties };
  for (const prop of readGated) {
    if (denied.has(prop.name)) {
      delete filteredProperties[prop.name];
      fastify?.log.debug(
        { sphereId: sphere.id, property: prop.name, required: prop.access!.read },
        '[applyAccessReadFilter] Stripped read-denied property'
      );
    }
  }

  return { ...sphere, properties: filteredProperties };
}
